import { FormQuestion, FormQuestionOption } from '../../Types/FormBuilder';
import { v4 as uuidv4 } from 'uuid';
import { supportedLocales } from '../../../I18n';
import { forEach, groupBy, partition } from 'lodash';

export const localizedAttributesFor = (attributeName: string, value = ''): any => {
  return supportedLocales.reduce(
    (acc, lang) => ({
      ...acc,
      [`${attributeName}_${lang}`]: value,
    }),
    {},
  );
};

export const generateFormQuestion = (props: any = {}): FormQuestion => ({
  field_type: 'text',
  required: false,
  newRecord: true,
  uuid_ref: uuidv4(),
  ...props,
  ...localizedAttributesFor('title'),
  ...localizedAttributesFor('description'),
  ...localizedAttributesFor('placeholder'),
});

export const generateQuestionOption = (other = false, value?: string): FormQuestionOption => ({
  other,
  newRecord: true,
  uuid_ref: uuidv4(),
  ...localizedAttributesFor('label', value),
});

/**
 * Iterates over the array of questions and fills `question_id`
 * for each non-section question with the id of the last previous section.
 */
export const assignSectionIds = (questions: FormQuestion[]) => {
  let lastSectionId: number | null = null;
  return questions.map((question: FormQuestion) => {
    if (question.field_type === 'section') {
      lastSectionId = question.id;
      question.section_id = null;
    } else {
      question.section_id = lastSectionId;
    }
    return question;
  });
};

/**
 * Groups questions by their question_id,
 * and separates the "section" from the associated questions.
 */

interface SectionGroup {
  section: FormQuestion;
  questions: FormQuestion[];
}

export const groupByQuestionId = (questions: FormQuestion[]): SectionGroup[] => {
  const FIRST_SECTION_KEY = 'first_section';
  const [sections, nonSections] = partition(questions, (q) => q.field_type === 'section');

  const grouped = groupBy(nonSections, (q) => q.section_id ?? FIRST_SECTION_KEY);

  return [
    // default section only if there are orphans
    ...(grouped[FIRST_SECTION_KEY]?.length
      ? [
          {
            section: { field_type: 'section' } as FormQuestion,
            questions: renumererQuestions(grouped[FIRST_SECTION_KEY]),
          },
        ]
      : []),
    // real sections
    ...sections.map((section) => ({
      section,
      questions: renumererQuestions(grouped[section.id] ?? []),
    })),
  ];
};

/**
 * Numbers a list of questions into sections and sub-questions.
 */
export const renumererQuestions = (questions: FormQuestion[], offset = 0) => {
  let mainCount = offset;
  let currentSectionId: number | null = null;
  let currentSectionNumber: string | null = null;
  const subCounters: Record<number, number> = {};

  return forEach(questions, (q) => {
    if (q.field_type === 'section') {
      // new root section
      mainCount += 1;
      q.number_label = `${mainCount}`;
      currentSectionId = q.id;
      currentSectionNumber = q.number_label;
      subCounters[currentSectionId] = 0;
    } else if (currentSectionId) {
      // sub-question: everything that follows a section until the next one
      subCounters[currentSectionId] += 1;
      q.number_label = `${currentSectionNumber}.${subCounters[currentSectionId]}`;
    } else {
      // isolated question (before any section)
      mainCount += 1;
      q.number_label = `${mainCount}`;
    }
  });
};
